/**
 * Learn — retention/engagement upgrade (Feb 2026 strategy).
 *
 * Headline systems:
 *   • Continue Watching — local-storage progress; shown only when the
 *     viewer actually has progress, so we don't show an empty rail.
 *   • Recommended Next — same-category, view-ranked, shown inside the modal.
 *   • Popular Lessons — three curated rails on the landing surface (Most
 *     Watched / Trending / Beginner Favourites), powered by /tutorials/sections.
 *   • My Bookmarks rail (auth) — server-stored via /tutorials/me/bookmarks.
 *   • Like + Bookmark — server-backed for authed users, falls back to a
 *     toast invite for anon.
 *   • Player upgrade — autoplay-next toggle (per-session), graceful
 *     fallback if iframe never fires onLoad, watch-on-youtube escape hatch.
 *   • Mobile-first cards w/ lazy-loaded thumbnails (`loading="lazy"` on
 *     <img>).
 *   • Creator attribution — every lesson defaults to "Presented by
 *     The Carp Cast" (architecture-ready for creators/sponsors/fisheries).
 *
 * Paywall is still OFF (LEARN_TIER_GATE_ENABLED=false). Tier badge is
 * cosmetic. Nothing here touches Stripe/subscriptions/competitions/etc.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  Play, BookOpen, Filter, Search, Clock, Eye, Star, Loader2, VideoOff,
  X, ChevronRight, Sparkles, ExternalLink, Heart, Bookmark,
  Flame, Trophy, History, Award, ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';
import TutorialThumb from '../components/TutorialThumb';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- local "Continue Watching" store ----------
const PROGRESS_KEY = 'cc_learn_progress';
const AUTOPLAY_KEY = 'cc_learn_autoplay_next';

// Content-version gate. Bump this string whenever a lesson_id's video
// has been swapped on the server side so any stale client-side cache
// entries for the affected ids get evicted on the user's next visit
// (no hard refresh required). The gate runs once at module load.
const CONTENT_VERSION = 'lcv-2026-05-21-stiff-link-repair';
const CONTENT_VERSION_KEY = 'cc_learn_content_version';
// Lesson ids whose video_url changed in this content version.
const VERSION_AFFECTED_IDS = ['knot-002', 'knot-005', 'knot-007', 'setup-001', 'setup-003', 'setup-004'];
(function evictStaleLearnCache() {
  try {
    const seen = localStorage.getItem(CONTENT_VERSION_KEY);
    if (seen === CONTENT_VERSION) return;
    // Prune affected ids from progress map (continue watching).
    try {
      const raw = localStorage.getItem(PROGRESS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          for (const id of VERSION_AFFECTED_IDS) {
            if (id in parsed) delete parsed[id];
          }
          localStorage.setItem(PROGRESS_KEY, JSON.stringify(parsed));
        }
      }
    } catch { /* corrupt JSON — readProgress() handles it */ }
    localStorage.setItem(CONTENT_VERSION_KEY, CONTENT_VERSION);
  } catch { /* quota / privacy mode — non-critical */ }
})();

// Only YouTube /embed/ URLs are considered renderable. Anything that
// doesn't match this shape (eg a stale row pointing at /api/uploads/...,
// or a typoed plain string) gets rejected before it can be assigned to
// an <iframe src> — protecting us from "Failed to load lesson" spinners
// and the corrupted-state crashes the user reported.
const YOUTUBE_EMBED_RE = /^https?:\/\/(?:www\.)?youtube\.com\/embed\/[\w-]{6,32}(?:\?.*)?$/;
const isPlayableVideoUrl = (url) => typeof url === 'string' && YOUTUBE_EMBED_RE.test(url);
const isValidLessonId = (id) => typeof id === 'string' && /^[\w-]{2,64}$/.test(id);

const readProgress = () => {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      // Garbage shape — wipe the slot so we stop re-parsing it every render.
      try { localStorage.removeItem(PROGRESS_KEY); } catch { /* noop */ }
      return {};
    }
    const clean = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (isValidLessonId(k) && v && typeof v === 'object' && typeof v.last_opened_at === 'number') {
        clean[k] = v;
      }
    }
    // Persist the cleaned shape so orphan/malformed entries are physically
    // pruned (not just filtered at read time). This satisfies the
    // self-healing contract for cc_learn_progress.
    if (Object.keys(clean).length !== Object.keys(parsed).length) {
      try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(clean)); } catch { /* quota */ }
    }
    return clean;
  } catch {
    // Outright malformed JSON — wipe the slot so the next page load is
    // already clean and we don't re-burn CPU on the same dead string.
    try { localStorage.removeItem(PROGRESS_KEY); } catch { /* noop */ }
    return {};
  }
};

const writeProgress = (data) => {
  try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(data)); }
  catch { /* quota — non-critical */ }
};

const markLessonOpened = (lessonId) => {
  if (!lessonId) return;
  const data = readProgress();
  data[lessonId] = { last_opened_at: Date.now() };
  writeProgress(data);
};

const formatViews = (views) => {
  if (!views) return '0';
  if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
  return String(views);
};

const tierBadgeClass = (tier) => ({
  basic: 'bg-[#2d352d] text-[#a8b1a8]',
  pro: 'bg-[#c8b273]/15 text-[#c8b273] border border-[#c8b273]/30',
  elite: 'bg-gradient-to-r from-[#c8b273]/30 to-[#7d6730]/30 text-[#c8b273] border border-[#c8b273]/40',
}[tier] || 'bg-[#2d352d] text-[#a8b1a8]');


// ============================================================
// MAIN COMPONENT
// ============================================================
const Learn = () => {
  const { user, token } = useAuth();
  const auth = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [lessons, setLessons] = useState([]);
  const [tips, setTips] = useState([]);
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [autoplayNext, setAutoplayNext] = useState(() => {
    try { return localStorage.getItem(AUTOPLAY_KEY) === '1'; } catch { return false; }
  });
  // AbortController for the in-flight openLesson request. If the user
  // clicks lesson B while A's GET is still in flight, we cancel A so the
  // late response can't overwrite B's modal state — the original
  // "modal opens the wrong video" + "stale payload" bug class.
  const inflightAbort = useRef(null);
  // Live-mount flag so async resolves never call setState after the page
  // has unmounted (defends against the 'cannot update unmounted
  // component' warning that could escalate into a render crash with
  // <StrictMode>).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (inflightAbort.current) {
        try { inflightAbort.current.abort(); } catch { /* noop */ }
      }
    };
  }, []);

  // initial fetch
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [lessonsRes, tipsRes, sectionsRes] = await Promise.all([
          axios.get(`${API}/tutorials`, { headers: auth }),
          axios.get(`${API}/tips`, { headers: auth }),
          axios.get(`${API}/tutorials/sections`, { headers: auth }).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setLessons(lessonsRes.data);
        setTips(tipsRes.data);
        setSections(sectionsRes?.data || null);
      } catch {
        if (!cancelled) toast.error('Failed to load lessons');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [auth]);

  // optimistic engagement state updates so the heart/bookmark icons feel instant
  const updateLessonInPlace = useCallback((id, patch) => {
    const upd = (rows) => rows.map((l) => ((l.id || l.session_id) === id ? { ...l, ...patch } : l));
    setLessons(upd);
    setSections((s) => s ? {
      most_watched: upd(s.most_watched || []),
      trending: upd(s.trending || []),
      beginner_favs: upd(s.beginner_favs || []),
      my_bookmarks: upd(s.my_bookmarks || []),
      spotlight: s.spotlight && (s.spotlight.id || s.spotlight.session_id) === id
        ? { ...s.spotlight, ...patch } : s.spotlight,
    } : s);
    setSelectedLesson((cur) => (cur && (cur.id || cur.session_id) === id ? { ...cur, ...patch } : cur));
  }, []);

  const openLesson = useCallback(async (lesson) => {
    const id = lesson?.session_id || lesson?.id;
    if (!isValidLessonId(id)) {
      toast.error("That lesson can't be opened — please refresh and try again.");
      return;
    }
    // Cancel any previous in-flight open so the response can't race ours
    // and overwrite the modal with a stale payload.
    if (inflightAbort.current) {
      try { inflightAbort.current.abort(); } catch { /* noop */ }
    }
    const ctrl = new AbortController();
    inflightAbort.current = ctrl;
    try {
      const r = await axios.get(`${API}/tutorials/${id}`, { headers: auth, signal: ctrl.signal });
      if (!mountedRef.current || ctrl.signal.aborted) return;
      const data = r?.data;
      // Strict payload validation — reject anything that wouldn't render.
      if (!data || !isValidLessonId(data.session_id || data.id) || !data.title) {
        toast.error('That lesson is unavailable right now.');
        return;
      }
      // Don't poison the iframe with a non-YouTube URL — the modal's
      // ComingSoonPlayer branch will render gracefully instead.
      if (data.video_url && !isPlayableVideoUrl(data.video_url)) {
        data.video_url = null;
      }
      setSelectedLesson(data);
      markLessonOpened(id);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return; // user moved on — silent.
      }
      if (mountedRef.current) toast.error('Failed to load lesson');
    } finally {
      // Only clear the ref if it's STILL pointing at our controller —
      // otherwise we'd clobber the newer click's controller.
      if (inflightAbort.current === ctrl) inflightAbort.current = null;
    }
  }, [auth]);

  const closeVideoModal = useCallback(() => {
    if (inflightAbort.current) {
      try { inflightAbort.current.abort(); } catch { /* noop */ }
      inflightAbort.current = null;
    }
    setSelectedLesson(null);
  }, []);

  const toggleLike = async (lesson) => {
    if (!token) {
      toast.info('Sign in to like lessons');
      return;
    }
    const id = lesson.session_id || lesson.id;
    const wasLiked = !!lesson.viewer_liked;
    updateLessonInPlace(id, {
      viewer_liked: !wasLiked,
      likes_count: (lesson.likes_count || 0) + (wasLiked ? -1 : 1),
    });
    try {
      if (wasLiked) await axios.delete(`${API}/tutorials/${id}/like`, { headers: auth });
      else await axios.post(`${API}/tutorials/${id}/like`, {}, { headers: auth });
    } catch {
      updateLessonInPlace(id, {
        viewer_liked: wasLiked,
        likes_count: lesson.likes_count,
      });
      toast.error('Could not update like');
    }
  };

  const toggleBookmark = async (lesson) => {
    if (!token) {
      toast.info('Sign in to save lessons');
      return;
    }
    const id = lesson.session_id || lesson.id;
    const wasBookmarked = !!lesson.viewer_bookmarked;
    updateLessonInPlace(id, {
      viewer_bookmarked: !wasBookmarked,
      bookmarks_count: (lesson.bookmarks_count || 0) + (wasBookmarked ? -1 : 1),
    });
    try {
      if (wasBookmarked) await axios.delete(`${API}/tutorials/${id}/bookmark`, { headers: auth });
      else await axios.post(`${API}/tutorials/${id}/bookmark`, {}, { headers: auth });
      // Refresh the bookmarks rail so the new entry appears immediately.
      const sec = await axios.get(`${API}/tutorials/sections`, { headers: auth });
      setSections(sec.data);
    } catch {
      updateLessonInPlace(id, {
        viewer_bookmarked: wasBookmarked,
        bookmarks_count: lesson.bookmarks_count,
      });
      toast.error('Could not update bookmark');
    }
  };

  const toggleAutoplay = () => {
    setAutoplayNext((v) => {
      const next = !v;
      try { localStorage.setItem(AUTOPLAY_KEY, next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };

  // Continue Watching → keep deterministic ordering by last_opened_at desc.
  // Self-prunes orphaned localStorage entries — if a lesson_id was removed
  // upstream (admin deletion, taxonomy rename, content map shake-up) we
  // strip it from the saved progress blob the moment we render so it can
  // never resurface as a ghost card or, worse, crash the page.
  const continueWatching = useMemo(() => {
    const progress = readProgress();
    if (!lessons.length) return [];
    const validIds = new Set(lessons.map((l) => l.id || l.session_id).filter(Boolean));
    const pruned = {};
    for (const [id, payload] of Object.entries(progress)) {
      if (validIds.has(id)) pruned[id] = payload;
    }
    if (Object.keys(pruned).length !== Object.keys(progress).length) {
      writeProgress(pruned);
    }
    return lessons
      .filter((l) => pruned[l.id || l.session_id])
      .map((l) => ({ ...l, _ts: pruned[l.id || l.session_id].last_opened_at }))
      .sort((a, b) => b._ts - a._ts)
      .slice(0, 8);
  }, [lessons]);

  const filterLessons = useCallback((category) => lessons.filter((lesson) => {
    const matchesCategory = lesson.category === category;
    const matchesSearch = !search
      || lesson.title.toLowerCase().includes(search.toLowerCase())
      || lesson.description.toLowerCase().includes(search.toLowerCase());
    const matchesDifficulty = !difficultyFilter || difficultyFilter === 'all'
      || lesson.difficulty === difficultyFilter;
    return matchesCategory && matchesSearch && matchesDifficulty;
  }), [lessons, search, difficultyFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050605] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#c8b273] animate-spin" />
      </div>
    );
  }

  const knotLessons = filterLessons('knots');
  const rigLessons = filterLessons('rigs');
  const setupLessons = filterLessons('setups');

  return (
    <div className="min-h-screen" data-testid="learn-page">
      {/* Hero */}
      <div
        className="relative h-64 bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1532724414795-8a873d116fab?w=1200')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#050605] via-[#050605]/70 to-transparent" />
        <div className="relative z-10 h-full flex items-end pb-8 px-6 md:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <p className="data-label text-[#c8b273] mb-2">Hints, Tips & Tricks</p>
            <h1 className="font-['Barlow_Condensed'] text-4xl font-extrabold uppercase tracking-wider text-white">
              Learn to Catch More Carp
            </h1>
            <p className="text-[#8a948a] mt-2">Master knots, rigs, and techniques with our video lessons</p>
          </div>
        </div>
      </div>

      {/* Community-driven Learn banner */}
      <div className="px-6 md:px-8 -mt-6 relative z-20">
        <div className="max-w-7xl mx-auto glass-card overflow-hidden border-[#c8b273]/30" data-testid="learn-free-banner">
          <div className="relative p-5 md:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#c8b273]/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-12 -left-12 w-44 h-44 bg-[#7d6730]/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex items-start gap-4">
              <div className="p-2.5 rounded-md bg-[#c8b273]/15 border border-[#c8b273]/30 flex-shrink-0">
                <Sparkles className="w-5 h-5 text-[#c8b273]" />
              </div>
              <div>
                <p className="data-label text-[#c8b273] mb-1">The Carp Cast Academy</p>
                <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-extrabold uppercase tracking-[0.04em] text-white leading-[1.05]">
                  Learn From Real Anglers
                </h2>
                <p className="text-[#c0c4c0] text-sm md:text-base mt-2 max-w-xl">
                  Tutorials, tactics and carp fishing mastery — all inside Carp Cast.
                </p>
              </div>
            </div>
            {!user && (
              <Link
                to="/register"
                className="relative inline-flex items-center justify-center gap-1.5 rounded bg-[#c8b273] hover:bg-[#d8c283] text-black text-xs font-bold uppercase tracking-widest px-4 py-2.5 transition-colors whitespace-nowrap"
                data-testid="learn-join-cta"
              >
                Join the community
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Continue Watching */}
      {continueWatching.length > 0 && (
        <SectionRail
          title="Continue Watching"
          subtitle="Pick up where you left off"
          icon={History}
          lessons={continueWatching}
          onOpen={openLesson}
          onLike={toggleLike}
          onBookmark={toggleBookmark}
          rail="continue-watching"
        />
      )}

      {/* Popular / Trending sections from server bundle */}
      {sections?.spotlight && (
        <SpotlightRow lesson={sections.spotlight} onOpen={openLesson} onBookmark={toggleBookmark} />
      )}
      {sections?.most_watched?.length > 0 && (
        <SectionRail
          title="Most Watched"
          subtitle="The lessons every angler comes back to"
          icon={Trophy}
          lessons={sections.most_watched}
          onOpen={openLesson}
          onLike={toggleLike}
          onBookmark={toggleBookmark}
          rail="most-watched"
        />
      )}
      {sections?.trending?.length > 0 && (
        <SectionRail
          title="Trending This Week"
          subtitle="Hot rigs & tactics in the community right now"
          icon={Flame}
          lessons={sections.trending}
          onOpen={openLesson}
          onLike={toggleLike}
          onBookmark={toggleBookmark}
          rail="trending"
        />
      )}
      {sections?.beginner_favs?.length > 0 && (
        <SectionRail
          title="Beginner Favourites"
          subtitle="Where to start if you're new to carp fishing"
          icon={Award}
          lessons={sections.beginner_favs}
          onOpen={openLesson}
          onLike={toggleLike}
          onBookmark={toggleBookmark}
          rail="beginner-favs"
        />
      )}
      {user && sections?.my_bookmarks?.length > 0 && (
        <SectionRail
          title="Your Bookmarks"
          subtitle="Lessons you've saved for later"
          icon={Bookmark}
          lessons={sections.my_bookmarks}
          onOpen={openLesson}
          onLike={toggleLike}
          onBookmark={toggleBookmark}
          rail="my-bookmarks"
        />
      )}

      {/* Filters + Full library */}
      <div className="sticky top-16 z-20 bg-[#050605]/95 backdrop-blur-sm border-y border-white/5 py-4 px-6 md:px-8 mt-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4a504a]" />
            <Input
              placeholder="Search lessons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-[#0b0d0b] border-white/10 text-white"
              data-testid="search-input"
            />
          </div>
          <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
            <SelectTrigger className="w-full sm:w-48 bg-[#0b0d0b] border-white/10 text-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Difficulty" />
            </SelectTrigger>
            <SelectContent className="bg-[#0b0d0b] border-white/10">
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="Beginner">Beginner</SelectItem>
              <SelectItem value="Intermediate">Intermediate</SelectItem>
              <SelectItem value="Advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="p-6 md:p-8">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="knots" className="w-full">
            <TabsList className="bg-[#0b0d0b] border border-white/10 mb-8 flex-wrap h-auto p-1">
              <TabsTrigger value="knots" className="data-[state=active]:bg-[#c8b273] data-[state=active]:text-black" data-testid="knots-tab">Knots</TabsTrigger>
              <TabsTrigger value="rigs" className="data-[state=active]:bg-[#c8b273] data-[state=active]:text-black" data-testid="rigs-tab">Rigs</TabsTrigger>
              <TabsTrigger value="setups" className="data-[state=active]:bg-[#c8b273] data-[state=active]:text-black" data-testid="setups-tab">Setups</TabsTrigger>
              <TabsTrigger value="tips" className="data-[state=active]:bg-[#c8b273] data-[state=active]:text-black" data-testid="tips-tab">Quick Tips</TabsTrigger>
            </TabsList>

            <LessonsTabContent value="knots" title="Master Essential Knots" subtitle="From basic to advanced, learn the knots that will land you more fish"
              lessons={knotLessons} onOpen={openLesson} onLike={toggleLike} onBookmark={toggleBookmark} />
            <LessonsTabContent value="rigs" title="Rig Setups & Presentations" subtitle="Complete guides to different carp rig setups" cols="md:grid-cols-2 lg:grid-cols-3"
              lessons={rigLessons} onOpen={openLesson} onLike={toggleLike} onBookmark={toggleBookmark} />
            <LessonsTabContent value="setups" title="Fishing Techniques & Setups" subtitle="Complete guides to different carp fishing approaches" cols="md:grid-cols-2 lg:grid-cols-3"
              lessons={setupLessons} onOpen={openLesson} onLike={toggleLike} onBookmark={toggleBookmark} />

            <TabsContent value="tips">
              <div className="mb-6">
                <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-2">Quick Tips & Tricks</h2>
                <p className="text-[#8a948a]">Bite-sized advice to improve your fishing</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tips.map((tip, index) => (
                  <motion.div
                    key={tip.id}
                    className="glass-card p-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.03, 0.3) }}
                    data-testid={`tip-card-${tip.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2 rounded ${tierBadgeClass(tip.tier)}`}>
                        <Star className="w-4 h-4" />
                      </div>
                    </div>
                    <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wide text-white mb-2">{tip.title}</h3>
                    <p className="text-sm text-[#8a948a]">{tip.description}</p>
                  </motion.div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {selectedLesson && (
          <LessonModal
            lesson={selectedLesson}
            token={token}
            autoplayNext={autoplayNext}
            onToggleAutoplay={toggleAutoplay}
            onClose={closeVideoModal}
            onLike={toggleLike}
            onBookmark={toggleBookmark}
            onOpenAnother={openLesson}
          />
        )}
      </AnimatePresence>
    </div>
  );
};


// ============================================================
// SECTION RAIL — horizontal scrollable strip
// ============================================================
const SectionRail = ({ title, subtitle, icon: Icon, lessons, onOpen, onLike, onBookmark, rail }) => (
  <div className="px-6 md:px-8 mt-8" data-testid={`section-${rail}`}>
    <div className="max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className="p-2 rounded-md bg-[#c8b273]/10 border border-[#c8b273]/20">
              <Icon className="w-4 h-4 text-[#c8b273]" />
            </div>
          )}
          <div>
            <h2 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase tracking-wider text-white">
              {title}
            </h2>
            {subtitle && <p className="text-[#8a948a] text-xs md:text-sm">{subtitle}</p>}
          </div>
        </div>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory -mx-1 px-1 scrollbar-thin">
        {lessons.map((lesson) => (
          <div key={`${rail}-${lesson.id || lesson.session_id}`} className="snap-start flex-shrink-0 w-[260px] md:w-[280px]">
            <LessonMiniCard lesson={lesson} onOpen={onOpen} onLike={onLike} onBookmark={onBookmark} testIdPrefix={`${rail}-card`} />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SpotlightRow = ({ lesson, onOpen, onBookmark }) => (
  <div className="px-6 md:px-8 mt-8" data-testid="section-spotlight">
    <div className="max-w-7xl mx-auto">
      <div
        className="glass-card overflow-hidden cursor-pointer group flex flex-col md:flex-row"
        onClick={() => onOpen(lesson)}
        data-testid="spotlight-card"
      >
        <div className="md:w-2/5 relative aspect-video md:aspect-auto">
          <TutorialThumb lesson={lesson} imgClassName="group-hover:scale-105" />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-14 h-14 bg-[#c8b273] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(200,178,115,0.45)]">
              <Play className="w-7 h-7 text-black ml-1" />
            </div>
          </div>
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-[#c8b273]/85 text-black">
            <Trophy className="w-3 h-3" /> Top Lesson
          </span>
        </div>
        <div className="flex-1 p-5 md:p-7 flex flex-col justify-center">
          <p className="data-label text-[#c8b273] mb-1">Spotlight</p>
          <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-extrabold uppercase tracking-wider text-white mb-2">
            {lesson.title}
          </h3>
          <p className="text-[#8a948a] text-sm md:text-base line-clamp-2 mb-3">{lesson.description}</p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#8a948a]">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {lesson.duration}</span>
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {formatViews(lesson.views)}</span>
            <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {lesson.likes_count || 0}</span>
            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${tierBadgeClass(lesson.tier)}`}>
              {lesson.tier}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Button className="btn-gold" onClick={(e) => { e.stopPropagation(); onOpen(lesson); }}>
              <Play className="w-4 h-4 mr-1" /> Watch now
            </Button>
            <button
              onClick={(e) => { e.stopPropagation(); onBookmark(lesson); }}
              className="text-[#c8b273] text-sm flex items-center gap-1 hover:underline"
              data-testid="spotlight-bookmark"
            >
              <Bookmark className={`w-4 h-4 ${lesson.viewer_bookmarked ? 'fill-[#c8b273]' : ''}`} />
              {lesson.viewer_bookmarked ? 'Saved' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);


// ============================================================
// LESSON CARDS
// ============================================================
const LessonMiniCard = ({ lesson, onOpen, onLike, onBookmark, testIdPrefix }) => {
  const id = lesson.session_id || lesson.id;
  return (
    <motion.div
      className="glass-card overflow-hidden cursor-pointer group h-full"
      onClick={() => onOpen(lesson)}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2 }}
      data-testid={`${testIdPrefix}-${id}`}
    >
      <TutorialThumb lesson={lesson} imgClassName="group-hover:scale-105">
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20">
          <div className="w-12 h-12 bg-[#c8b273] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(200,178,115,0.45)]">
            <Play className="w-6 h-6 text-black ml-0.5" />
          </div>
        </div>
        <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-[10px] text-white flex items-center gap-1 z-10">
          <Clock className="w-2.5 h-2.5" />
          {lesson.duration}
        </div>
        <div className={`absolute top-2 left-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase z-10 ${tierBadgeClass(lesson.tier)}`}>
          {lesson.tier}
        </div>
      </TutorialThumb>
      <div className="p-3">
        <h3 className="font-['Barlow_Condensed'] text-base font-bold uppercase tracking-wide text-white group-hover:text-[#c8b273] transition-colors line-clamp-2">
          {lesson.title}
        </h3>
        <div className="flex items-center justify-between mt-2 text-[11px] text-[#6b7d6b]">
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {formatViews(lesson.views)}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onLike?.(lesson); }}
              className={`flex items-center gap-0.5 hover:text-[#c8b273] transition-colors ${lesson.viewer_liked ? 'text-[#c8b273]' : ''}`}
              data-testid={`${testIdPrefix}-like-${id}`}
              aria-label="Like lesson"
            >
              <Heart className={`w-3 h-3 ${lesson.viewer_liked ? 'fill-[#c8b273]' : ''}`} />
              {lesson.likes_count || 0}
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onBookmark?.(lesson); }}
              className={`hover:text-[#c8b273] transition-colors ${lesson.viewer_bookmarked ? 'text-[#c8b273]' : ''}`}
              data-testid={`${testIdPrefix}-bookmark-${id}`}
              aria-label="Bookmark lesson"
            >
              <Bookmark className={`w-3 h-3 ${lesson.viewer_bookmarked ? 'fill-[#c8b273]' : ''}`} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const LessonCard = ({ lesson, onOpen, onLike, onBookmark }) => {
  const id = lesson.session_id || lesson.id;
  return (
    <motion.div
      className="glass-card overflow-hidden cursor-pointer group"
      onClick={() => onOpen(lesson)}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      data-testid={`lesson-card-${id}`}
    >
      <TutorialThumb lesson={lesson} imgClassName="group-hover:scale-105" testId={`lesson-thumb-${id}`}>
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20">
          <div className="w-16 h-16 bg-[#c8b273] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(200,178,115,0.45)]">
            <Play className="w-8 h-8 text-black ml-1" />
          </div>
        </div>
        <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs text-white flex items-center gap-1 z-10">
          <Clock className="w-3 h-3" />
          {lesson.duration}
        </div>
        <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-bold uppercase z-10 ${tierBadgeClass(lesson.tier)}`}>
          {lesson.tier}
        </div>
      </TutorialThumb>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-xs px-2 py-0.5 rounded ${
            lesson.difficulty === 'Beginner' ? 'bg-green-500/20 text-green-400'
              : lesson.difficulty === 'Intermediate' ? 'bg-yellow-500/20 text-yellow-400'
              : 'bg-red-500/20 text-red-400'
          }`}>
            {lesson.difficulty}
          </span>
          <span className="text-xs text-[#6b7d6b] uppercase">{id}</span>
        </div>
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wide text-white group-hover:text-[#c8b273] transition-colors">
          {lesson.title}
        </h3>
        <p className="text-[#8a948a] text-sm mt-2 line-clamp-2">{lesson.description}</p>
        <div className="flex items-center justify-between mt-3 text-xs text-[#6b7d6b]">
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {formatViews(lesson.views)} views</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onLike?.(lesson); }}
              className={`flex items-center gap-1 hover:text-[#c8b273] transition-colors ${lesson.viewer_liked ? 'text-[#c8b273]' : ''}`}
              data-testid={`lesson-like-${id}`}
            >
              <Heart className={`w-3.5 h-3.5 ${lesson.viewer_liked ? 'fill-[#c8b273]' : ''}`} />
              <span>{lesson.likes_count || 0}</span>
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onBookmark?.(lesson); }}
              className={`hover:text-[#c8b273] transition-colors ${lesson.viewer_bookmarked ? 'text-[#c8b273]' : ''}`}
              data-testid={`lesson-bookmark-${id}`}
              aria-label={lesson.viewer_bookmarked ? 'Remove bookmark' : 'Bookmark lesson'}
            >
              <Bookmark className={`w-3.5 h-3.5 ${lesson.viewer_bookmarked ? 'fill-[#c8b273]' : ''}`} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const LessonsTabContent = ({
  value, title, subtitle, lessons, cols = 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  onOpen, onLike, onBookmark,
}) => (
  <TabsContent value={value}>
    <div className="mb-6">
      <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-2">{title}</h2>
      <p className="text-[#8a948a]">{subtitle}</p>
    </div>
    {lessons.length === 0 ? (
      <div className="glass-card p-12 text-center">
        <BookOpen className="w-16 h-16 text-[#4a504a] mx-auto mb-4" />
        <p className="text-[#8a948a]">No lessons match your search</p>
      </div>
    ) : (
      <div className={`grid ${cols} gap-6`}>
        {lessons.map((lesson, index) => (
          <motion.div
            key={lesson.session_id || lesson.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(index * 0.03, 0.3) }}
          >
            <LessonCard lesson={lesson} onOpen={onOpen} onLike={onLike} onBookmark={onBookmark} />
          </motion.div>
        ))}
      </div>
    )}
  </TabsContent>
);


// ============================================================
// MODAL with autoplay, fallback, related lessons
// ============================================================
const LessonModal = ({
  lesson, token, autoplayNext, onToggleAutoplay, onClose,
  onLike, onBookmark, onOpenAnother,
}) => {
  const [related, setRelated] = useState([]);

  // Fetch related lessons. If the API hiccups it's not a blocker — modal
  // still works without the strip. Aborts on lesson change/unmount so a
  // late response can't leak into a fresh modal.
  useEffect(() => {
    const ctrl = new AbortController();
    const id = lesson.session_id || lesson.id;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/tutorials/${id}/related`, { headers, params: { limit: 3 }, signal: ctrl.signal })
      .then((r) => { if (!ctrl.signal.aborted) setRelated(r.data || []); })
      .catch(() => { if (!ctrl.signal.aborted) setRelated([]); });
    return () => { ctrl.abort(); };
  }, [lesson.session_id, lesson.id, token]);

  // Autoplay next: when the video URL changes, no-op; when user wants
  // autoplay-next we add ?autoplay=1 to the iframe src. NB: this is best-
  // effort — YouTube respects autoplay only when the embed is allowed.
  // We also re-validate the URL here so a stale state path can't ever
  // assign a malformed value into an <iframe src>.
  const iframeSrc = useMemo(() => {
    if (!lesson.video_url) return null;
    if (!YOUTUBE_EMBED_RE.test(lesson.video_url)) return null;
    const sep = lesson.video_url.includes('?') ? '&' : '?';
    return autoplayNext ? `${lesson.video_url}${sep}autoplay=1` : lesson.video_url;
  }, [lesson.video_url, autoplayNext]);

  const handleAutoplayPick = (next) => {
    if (!next || !autoplayNext) {
      onClose();
      // Open the next lesson AFTER the modal closes so the fresh state
      // doesn't race the previous iframe teardown.
      setTimeout(() => onOpenAnother?.(next), 80);
      return;
    }
    onOpenAnother?.(next);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      data-testid="lesson-modal"
    >
      <div className="absolute inset-0 bg-black/85" />
      <motion.div
        className="relative bg-[#0b0d0b] border border-white/10 rounded-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-30 p-2 rounded-full bg-black/60 hover:bg-black/80 transition-colors"
          data-testid="close-video-modal"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        <div className="aspect-video bg-black">
          {iframeSrc ? (
            <LessonVideoPlayer videoSrc={iframeSrc} lesson={lesson} />
          ) : (
            <ComingSoonPlayer />
          )}
        </div>

        <div className="p-5 sm:p-6">
          {/* Title + metadata */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="text-xs text-[#6b7d6b] uppercase font-mono bg-[#141714] px-2 py-1 rounded">
              {lesson.session_id || lesson.id}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded ${
              lesson.difficulty === 'Beginner' ? 'bg-green-500/20 text-green-400'
                : lesson.difficulty === 'Intermediate' ? 'bg-yellow-500/20 text-yellow-400'
                : 'bg-red-500/20 text-red-400'
            }`}>{lesson.difficulty}</span>
            <span className="text-[#8a948a] text-sm flex items-center gap-1">
              <Clock className="w-3 h-3" /> {lesson.duration}
            </span>
            <span className="text-[#8a948a] text-sm flex items-center gap-1">
              <Eye className="w-3 h-3" /> {formatViews(lesson.views)} views
            </span>
            <span className={`text-xs px-2 py-0.5 rounded uppercase ${tierBadgeClass(lesson.tier)}`}>
              {lesson.tier}
            </span>
          </div>
          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-1">
            {lesson.title}
          </h2>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#c8b273] mb-3" data-testid="lesson-creator">
            Presented by {lesson.creator_name || 'The Carp Cast'}
          </p>
          <p className="text-[#8a948a]">{lesson.description}</p>

          {/* Engagement strip */}
          <div className="mt-5 flex flex-wrap items-center gap-3 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={() => onLike?.(lesson)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded border text-sm transition-colors ${
                lesson.viewer_liked
                  ? 'bg-[#c8b273]/15 border-[#c8b273]/40 text-[#c8b273]'
                  : 'border-white/10 text-[#c0c4c0] hover:border-[#c8b273]/40 hover:text-white'
              }`}
              data-testid="modal-like-btn"
            >
              <Heart className={`w-4 h-4 ${lesson.viewer_liked ? 'fill-[#c8b273]' : ''}`} />
              <span>{lesson.likes_count || 0}</span>
              <span className="hidden sm:inline">{lesson.viewer_liked ? 'Liked' : 'Like'}</span>
            </button>
            <button
              type="button"
              onClick={() => onBookmark?.(lesson)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded border text-sm transition-colors ${
                lesson.viewer_bookmarked
                  ? 'bg-[#c8b273]/15 border-[#c8b273]/40 text-[#c8b273]'
                  : 'border-white/10 text-[#c0c4c0] hover:border-[#c8b273]/40 hover:text-white'
              }`}
              data-testid="modal-bookmark-btn"
            >
              <Bookmark className={`w-4 h-4 ${lesson.viewer_bookmarked ? 'fill-[#c8b273]' : ''}`} />
              <span className="hidden sm:inline">{lesson.viewer_bookmarked ? 'Saved' : 'Save for later'}</span>
            </button>
            <label className="ml-auto inline-flex items-center gap-2 text-xs text-[#8a948a] cursor-pointer select-none" data-testid="autoplay-toggle">
              <input
                type="checkbox"
                checked={autoplayNext}
                onChange={onToggleAutoplay}
                className="accent-[#c8b273]"
              />
              Autoplay next
            </label>
          </div>

          {/* Recommended next strip */}
          {related.length > 0 && (
            <div className="mt-6 pt-5 border-t border-white/10" data-testid="related-strip">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-[#c8b273]" />
                  Recommended next
                </h3>
                <span className="text-[10px] uppercase tracking-widest text-[#6b7d6b]">Same category</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {related.map((r) => (
                  <button
                    key={r.id || r.session_id}
                    onClick={() => handleAutoplayPick(r)}
                    className="text-left bg-[#141714] border border-[#1f221f] rounded-lg overflow-hidden hover:border-[#c8b273]/50 transition-colors group"
                    data-testid={`related-lesson-${r.id || r.session_id}`}
                  >
                    <div className="aspect-video relative">
                      <TutorialThumb lesson={r} imgClassName="group-hover:scale-105" />
                      <div className="absolute bottom-1.5 right-1.5 bg-black/80 px-1.5 py-0.5 rounded text-[10px] text-white flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> {r.duration}
                      </div>
                    </div>
                    <div className="p-2.5">
                      <p className="text-white text-xs font-bold uppercase tracking-wide line-clamp-2">{r.title}</p>
                      <p className="text-[10px] text-[#6b7d6b] mt-0.5">{formatViews(r.views)} views · {r.difficulty}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
            <span className="text-xs text-[#6b7d6b] uppercase">Category: {lesson.category}</span>
            {lesson.video_url && (
              <a
                href={lesson.video_url.replace('/embed/', '/watch?v=')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#c8b273] hover:underline inline-flex items-center gap-1"
                data-testid="lesson-watch-on-youtube"
              >
                <ExternalLink className="w-3 h-3" /> Open on YouTube
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};


// ============================================================
// VIDEO PLAYER
// ============================================================
const LessonVideoPlayer = ({ videoSrc, lesson }) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setFailed(false);
    setLoaded(false);
    timeoutRef.current = setTimeout(() => {
      if (!loaded) setFailed(true);
    }, 6000);
    return () => clearTimeout(timeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoSrc]);

  const onLoad = () => {
    setLoaded(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  if (failed) return <VideoUnavailableFallback lesson={lesson} videoSrc={videoSrc} />;
  return (
    <iframe
      key={videoSrc}
      src={videoSrc}
      title={lesson.title}
      className="w-full h-full"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      onLoad={onLoad}
      onError={() => setFailed(true)}
      data-testid="lesson-video-frame"
    />
  );
};

const ComingSoonPlayer = () => (
  <div className="w-full h-full flex flex-col items-center justify-center bg-[#141714]" data-testid="lesson-video-coming-soon">
    <VideoOff className="w-20 h-20 text-[#4a504a] mb-4" />
    <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-2">Video Coming Soon</h3>
    <p className="text-[#8a948a] text-center max-w-md">We're working on this tutorial. Check back soon for the full video lesson.</p>
  </div>
);

const VideoUnavailableFallback = ({ lesson, videoSrc }) => {
  const watchUrl = (() => {
    try {
      const src = videoSrc || lesson.video_url;
      const m = /\/embed\/([\w-]+)/.exec(src || '');
      return m ? `https://www.youtube.com/watch?v=${m[1]}` : null;
    } catch { return null; }
  })();
  return (
    <div className="relative w-full h-full overflow-hidden" data-testid="lesson-video-unavailable">
      <div className="absolute inset-0">
        <TutorialThumb lesson={lesson} showWatermark={false} />
      </div>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px] flex flex-col items-center justify-center text-center px-6">
        <VideoOff className="w-12 h-12 text-[#c8b273] mb-3" />
        <h3 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase tracking-wider text-white mb-2">Player couldn't load</h3>
        <p className="text-[#c0c4c0] text-sm max-w-md">The embedded video didn't open in your browser. You can still watch the full lesson on YouTube.</p>
        {watchUrl && (
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded bg-[#c8b273] hover:bg-[#d8c283] text-black text-xs font-bold uppercase tracking-widest transition-colors"
            data-testid="lesson-watch-on-youtube-fallback"
          >
            <ExternalLink className="w-4 h-4" />
            Watch on YouTube
          </a>
        )}
      </div>
    </div>
  );
};

export default Learn;
